import { createSlice, createAsyncThunk, PayloadAction, current } from "@reduxjs/toolkit";
import { getWorkOrderDetails, createAssetApi, updateAssetStatusApi } from "../services/workOrderDetailApi";
import { calculateTotalVerifiedModules, calculateTotalVerifiedPoints, processAsset, processWorkOrders } from "../../lib/workOrderItems";
import { AppDispatch, RootState } from "../store";
import { incrementAssetCount, updateCompletedAssetCount } from "./workOrderSlice";
import { WorkOrderStatus } from "../../types/workOrder";
import { showToaster } from "./toasterSlice";
import { isReallyOnline } from "../../lib/isOnline";
import { Detail, Section } from "../../types/inspection";
import { isPointCompleted } from "../../lib/inspectionItems";

export interface WorkOrderDetail {
    assetNumber: string;
    stockNumber: string;
    id: number;
    description: string;
    imageURL: string;
    inspectionLevel: string;
    inspectionLevel_ID: number;
    totalSumOfPoints: number;
    item_ID: number;
    status_ID: number;
    type: number;
    totalVerified: number;
    totalVerifiedModules: number;
    vin: number;
    modules: any[];
    photosCount: number;
}

export interface CreatedWorkOrder {
    workorder_id: number;
    createdAsset: WorkOrderDetail;
}



export interface WorkOrderDetailState {
    workOrderDetail: {
        [workorder_id: number]: {
            items: WorkOrderDetail[],
            workOrderStatus: WorkOrderStatus;
            totalAssets: number;
        };
    };
    loading: boolean;
    error: string | null;
}

const initialState: WorkOrderDetailState = {
    workOrderDetail: {},
    loading: false,
    error: null,
};

export const updateAssetStatus = createAsyncThunk<
    string,
    { token: string | null; asset_id: number; status: number; workOrder_ID: number; data: any; shouldNotify?: boolean; shouldUpdateCompleteAssetCount: boolean },
    { rejectValue: string; state: RootState; dispatch: AppDispatch }
>(
    "inspectionPoint/updateAssetStatus",
    async (
        { token, asset_id, status, workOrder_ID, data, shouldNotify = false, shouldUpdateCompleteAssetCount },
        { rejectWithValue, dispatch, getState }
    ) => {
        try {
            const { workOrders } = getState().workOrders;
            const matchingOrder = workOrders.find(
                (order) => order.workOrder_ID === workOrder_ID
            );
            if (matchingOrder && matchingOrder.workOrderStatus === WorkOrderStatus.Completed) {
                return;
            }
            const updatedAssetStatusResult = await updateAssetStatusApi(
                token,
                asset_id,
                status,
                data
            );

            dispatch(
                updateAssetStatusInStore({
                    asset_id,
                    status_ID: status,
                    workOrder_ID
                })
            );

            let change = 0;
            if (status === 3 || status === 4) {
                change = 1;
            } else if (status === 1) {
                change = -1;
            }

            if (change !== 0 && shouldUpdateCompleteAssetCount) {
                dispatch(updateCompletedAssetCount({ workOrder_ID, change }));
            }
            if (shouldNotify) {
                const isOnline = await isReallyOnline()
                dispatch(showToaster({ message: isOnline ? `Item ${status == 4 ? "skipped" : "unskipped"}!` : `Item ${status == 4 ? "skipped" : "unskipped"} offline!` , type: "success" }));
            }

            return updatedAssetStatusResult;
        } catch (error: any) {
            // dispatch(showToaster({ message: "Error while updating item", type: "error" }));

            console.error("[Error in updating asset status]::", error);
            return rejectWithValue("Failed to update asset status");
        }
    }
);

export const fetchWorkOrderDetails = createAsyncThunk<
    { workOrderDetail: WorkOrderDetail[]; workOrderStatus?: WorkOrderStatus, workorder_id: number },
    { token: string | null; workorder_id: number },
    { rejectValue: string; state: RootState }
>(
    "assets/fetchWorkOrderDetails",
    async ({ token, workorder_id }, { rejectWithValue, getState }) => {
        try {
            const workOrderDetail = await getWorkOrderDetails(token, workorder_id);
            const { workOrders } = getState().workOrders;

            const matchingOrder = workOrders.find(
                (order) => order.workOrder_ID === workorder_id
            );

            const workOrderStatus =
                matchingOrder?.workOrderStatus as WorkOrderStatus | undefined;

            return { workOrderDetail, workOrderStatus, workorder_id };
        } catch (error) {
            console.error("[Error in Fetching work orders]::", error);
            return rejectWithValue("Failed to fetch work orders");
        }
    }
);

export const createAsset = createAsyncThunk<
    CreatedWorkOrder,
    {
        token: string | null;
        workorder_id: number;
        assetData: {
            type_ID: number;
            category_ID: number;
            family_ID: number;
            useYearMakeModel: boolean;
            description: string;
            year: string;
            make: string;
            model: string;
            unitOfMeasure_ID: number;
            length: string;
            quantity: string;
            assetNumber: string;
            serialNumber: string;
            stockNumber: string;
            template: any;
            creationKey: string;
        };
    },
    { dispatch: AppDispatch; state: RootState }
>(
    "assets/createAsset",
    async ({ token, workorder_id, assetData }, { dispatch, rejectWithValue }) => {
        try {
            const createdAsset = await createAssetApi(token, workorder_id, assetData);
            const isOnline = await isReallyOnline()
            dispatch(showToaster({ message: isOnline ? "Item created successfully!" : "Item created offline!", type: "success" }));
            dispatch(incrementAssetCount({ workOrder_ID: workorder_id }));
            return { workorder_id, createdAsset };
        } catch (error) {
            dispatch(showToaster({ message: "Error while creating", type: "error" }));

            console.log("[Error in Fetching work orders]:: ", error);
            return rejectWithValue("Failed to fetch work orders");
        }
    }
);


const workOrderSlice = createSlice({
    name: "workOrders",
    initialState,
    reducers: {
        removeWorkOrderDetail: (state) => {
            state.workOrderDetail = {};
            state.loading = false;
            state.error = null;
        },
        updateWorkOrderDetail: (
            state,
            action: PayloadAction<{ item_ID: number; updatePoint: Detail, sectionIndex: number, workorder_id: number }>
        ) => {
            const { item_ID, updatePoint, sectionIndex, workorder_id } = action.payload;
            const index = current(state).workOrderDetail[workorder_id].items.findIndex(
                (order) => order.item_ID === item_ID
            );

            if (index !== -1) {
                const modules =
                    state.workOrderDetail[workorder_id].items[index].modules;

                if (modules && modules[sectionIndex]) {
                    const points = modules[sectionIndex].points;

                    const pointIndex = points.findIndex((point: Detail) => point.id === updatePoint.id);
                    if (pointIndex !== -1) {
                        points[pointIndex] = {
                            ...points[pointIndex],
                            ...updatePoint,
                        };

                        let verifiedCount = modules[sectionIndex].totalVerified || 0;

                        const isCompleted = isPointCompleted(points[pointIndex], current(state).workOrderDetail[workorder_id].items[index].type);

                        if (isCompleted && !points[pointIndex].isCompleted) {
                            verifiedCount++;
                        } else if (!isCompleted && points[pointIndex].isCompleted) {
                            verifiedCount--;
                        }
                        points[pointIndex] = {
                            ...points[pointIndex],
                            isCompleted,
                        };
                        const isSkipped = points.every((point: any) => point.notApplicable === true);

                        modules[sectionIndex] = {
                            ...modules[sectionIndex],
                            totalVerified: verifiedCount,
                            points: [...points],
                            isSkipped
                        };
                    }
                }

                const totalVerifiedPoints = calculateTotalVerifiedPoints(modules);
                const totalVerifiedModules = calculateTotalVerifiedModules(modules);

                state.workOrderDetail[workorder_id].items[index].totalVerified = totalVerifiedPoints;
                state.workOrderDetail[workorder_id].items[index].totalVerifiedModules = totalVerifiedModules;

                state.workOrderDetail[workorder_id].items[index].modules = [...modules];
            }
        },
        updateWorkOrderDetailWithModule:  (
            state,
            action: PayloadAction<{ item_ID: number; updatedModule: Section, sectionIndex:number, workorder_id: number }>
        ) => {
            const { item_ID, updatedModule, sectionIndex, workorder_id } = action.payload;
            const index = current(state).workOrderDetail[workorder_id].items.findIndex(
                (order) => order.item_ID === item_ID
            );

            if (index !== -1 && state.workOrderDetail[workorder_id].items[index]) {
                let modules =
                    state.workOrderDetail[workorder_id].items[index].modules;

                    modules=modules.map((section: Section, index: number)=>{
                        if(index==sectionIndex){
                            return {
                                ...section,
                                ...updatedModule,
                            }
                        }
                        else {
                            return {
                                ...section,
                                isOpen: false
                            }
                        }
                    })

                const totalVerifiedPoints = calculateTotalVerifiedPoints(modules);
                const totalVerifiedModules = calculateTotalVerifiedModules(modules);

                state.workOrderDetail[workorder_id].items[index].totalVerified = totalVerifiedPoints;
                state.workOrderDetail[workorder_id].items[index].totalVerifiedModules = totalVerifiedModules;
                state.workOrderDetail[workorder_id].items[index].modules = modules;
            }
        },
        updateAssetStatusInStore: (
            state,
            action: PayloadAction<{ asset_id: number; status_ID: number, workOrder_ID: number }>
        ) => {
            const { asset_id, status_ID, workOrder_ID } = action.payload;
            const assetIndex = state.workOrderDetail[workOrder_ID].items.findIndex(
                (asset) => asset.id === asset_id
            );

            if (assetIndex !== -1) {
                state.workOrderDetail[workOrder_ID].items[assetIndex].status_ID = status_ID;
            }
        },
        updateImageCount: (
            state,
            action: PayloadAction<{ asset_id: number; photosCount: number, workOrder_ID: number }>
        ) => {
            const { asset_id, photosCount, workOrder_ID } = action.payload;
            console.log("Payload: ",action.payload)
            const assetIndex = state.workOrderDetail[workOrder_ID].items.findIndex(
                (asset) => asset.item_ID === asset_id
            );
            console.log("Asset Index: ", assetIndex)

            if (assetIndex !== -1) {
                state.workOrderDetail[workOrder_ID].items[assetIndex].photosCount = photosCount;
            }
        },
        replaceTempItemId: (
            state,
            action: PayloadAction<{ tempId: number; realId: number; workorder_id: number }>
        ) => {
            const { tempId, realId, workorder_id } = action.payload;
            const assetIndex = state.workOrderDetail[workorder_id].items.findIndex(
                (asset) => asset.item_ID === tempId
            );

            if (assetIndex !== -1) {
                state.workOrderDetail[workorder_id].items[assetIndex].item_ID = realId;
            }
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchWorkOrderDetails.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                fetchWorkOrderDetails.fulfilled,
                (
                    state,
                    action: PayloadAction<{
                        workOrderDetail: WorkOrderDetail[];
                        workOrderStatus?: WorkOrderStatus;
                        workorder_id: number;
                    }>
                ) => {
                    const { workOrderDetail, workOrderStatus, workorder_id } = action.payload;
                    const processedWorkOrders = processWorkOrders(
                        workOrderDetail
                    );

                    state.workOrderDetail = {
                        ...state.workOrderDetail,
                        [workorder_id]: { ...state.workOrderDetail[workorder_id], items: processedWorkOrders, totalAssets: workOrderDetail.length, workOrderStatus: workOrderStatus ?? WorkOrderStatus.InProgress },
                    };
                    state.loading = false;
                }
            )
            .addCase(fetchWorkOrderDetails.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            })
            .addCase(createAsset.pending, (state) => {
                state.loading = true;
                state.error = null;
            })
            .addCase(
                createAsset.fulfilled,
                (state, action: PayloadAction<CreatedWorkOrder>) => {
                    const { createdAsset, workorder_id } = action.payload
                    const newWorkOrder = {
                        ...createdAsset,
                        // totalVerified: calculateTotalVerifiedPoints(action.payload.modules),
                    };
                    const processedAsset= processAsset(newWorkOrder);
                    state.workOrderDetail[workorder_id].items.push(processedAsset);
                    state.workOrderDetail[workorder_id].totalAssets += 1;
                    state.loading = false;
                }
            )
            .addCase(createAsset.rejected, (state, action) => {
                state.loading = false;
                state.error = action.payload as string;
            });
    },
});

export const { removeWorkOrderDetail, updateWorkOrderDetailWithModule, updateWorkOrderDetail, updateAssetStatusInStore, replaceTempItemId, updateImageCount } = workOrderSlice.actions;
export default workOrderSlice.reducer;
